import type {Moment, unitOfTime} from 'moment-timezone';
import moment from 'moment-timezone';

import {type SelectOption} from '@famly/mf_input_select';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {getClosestBiweeklyStartDate} from './create-billing-periods';
import type * as Types from '../types';

// As decided by the designer, we render 53 periods to cover one year (plus a few extra days) for weekly billing.
// This is a temporary solution until the date picker is implemented.
const DEFAULT_NUMBER_OF_OPTIONS_DISPLAYED = 53;

type BiweeklyConfig =
    | {intervalMultiplier: number; biweeklyCadenceStartDate: Moment}
    | {intervalMultiplier?: undefined; biweeklyCadenceStartDate?: undefined};

const generateOptions = ({
    type,
    dateRange,
    unitOfTime,
    format,
    intervalMultiplier = 1,
    biweeklyCadenceStartDate,
}: {
    type: 'start' | 'end';
    dateRange: {startDate: Moment; endDate: null | Moment};
    unitOfTime: unitOfTime.DurationConstructor;
    format: string;
} & BiweeklyConfig): SelectOption[] => {
    const generateDate = (date: Moment) => {
        if (hasValue(biweeklyCadenceStartDate) && type === 'end') {
            // It ensures the correct period end date when the start date falls in the middle of a biweekly period.
            // If the start date aligns with a biweekly period start date, return the end of the next week.
            // Otherwise, determine the closest biweekly start date based on the cadence:
            // - If the start date is in the same week as the biweekly cadence start date, return the end of the next week.
            // - If it's not in the same week, return the end of the week the start date falls in.

            const closestBiweeklyStartDate = getClosestBiweeklyStartDate(biweeklyCadenceStartDate, date);

            const isDateStartOfPeriod = isDateSameAsPeriodBoundary(date, closestBiweeklyStartDate);

            const isInSameWeek = closestBiweeklyStartDate.isSame(date, 'isoWeek');

            const nextEndDate = isInSameWeek
                ? date.clone().add(1, 'weeks').endOf('isoWeek')
                : date.clone().endOf('isoWeek');

            return isDateStartOfPeriod ? date.clone().add(1, 'weeks').endOf('isoWeek') : nextEndDate;
        }

        return date.clone()[type === 'start' ? 'startOf' : 'endOf'](unitOfTime === 'weeks' ? 'isoWeek' : 'month');
    };

    const numberOfDates = ((): number => {
        if (hasValue(dateRange.endDate)) {
            return Math.ceil(dateRange.endDate.diff(dateRange.startDate, unitOfTime, true) / intervalMultiplier);
        }

        return DEFAULT_NUMBER_OF_OPTIONS_DISPLAYED;
    })();

    // This guarantees that even if numberOfDates is 0 or negative, it won't break.
    const periodIndices = [...Array(Math.max(0, numberOfDates)).keys()];

    const getNextPeriod = (date: Moment) => (index: number) => date.clone().add(index * intervalMultiplier, unitOfTime);

    return periodIndices
        .map(getNextPeriod(dateRange.startDate))
        .filter(date => {
            // Start date options can only be before the end date if an end date is available
            if (type === 'start' && hasValue(dateRange.endDate)) {
                return date.isSameOrBefore(dateRange.endDate);
            }

            return true;
        })
        .map(date => ({
            label: generateDate(date).format(format),
            value: generateDate(date).format('YYYY-MM-DD'),
        }));
};

const generatePlanStartDateOptions = ({
    optionsDateRange,
    billingFrequency,
    biweeklyCadenceStartDate,
    planStartDate,
}: {
    optionsDateRange: {startDate: Types.InitialStartDate; endDate: null | Moment};
    billingFrequency: Types.InitialBillingFrequency;
    biweeklyCadenceStartDate: Moment;
    planStartDate: Moment;
}): SelectOption[] => {
    const optionsStartDate = getOptionsStartDate({
        optionsDateRange,
        billingFrequency,
        biweeklyCadenceStartDate,
    });
    const originalStartDateOption: SelectOption = {
        label: planStartDate.format('MMMM D, YYYY'),
        value: planStartDate.format('YYYY-MM-DD'),
    };

    const options = (() => {
        switch (billingFrequency) {
            case 'monthly': {
                return generateOptions({
                    type: 'start',
                    dateRange: {startDate: optionsStartDate, endDate: optionsDateRange.endDate},
                    unitOfTime: 'months',
                    format: 'MMMM YYYY',
                });
            }
            case 'weekly':
                return generateOptions({
                    type: 'start',
                    dateRange: {startDate: optionsStartDate, endDate: optionsDateRange.endDate},
                    unitOfTime: 'weeks',
                    format: 'MMMM D, YYYY',
                });
            case 'biweekly':
                return generateOptions({
                    type: 'start',
                    dateRange: {startDate: optionsStartDate, endDate: optionsDateRange.endDate},
                    unitOfTime: 'weeks',
                    format: 'MMMM D, YYYY',
                    intervalMultiplier: 2,
                    biweeklyCadenceStartDate,
                });
            default:
                return [];
        }
    })();

    const isInitialStartDateOptionPartOfOptions = options.some(option =>
        moment(option.value).isSame(moment(originalStartDateOption.value)),
    );

    return isInitialStartDateOptionPartOfOptions ? options : sortOptionsByDate([originalStartDateOption, ...options]);
};
const getOptionsStartDate = ({
    optionsDateRange,
    billingFrequency,
    biweeklyCadenceStartDate,
}: {
    optionsDateRange: {startDate: Types.InitialStartDate; endDate: null | Moment};
    billingFrequency: Types.InitialBillingFrequency;
    biweeklyCadenceStartDate: Moment;
}): Moment => {
    const closestPeriodStartDate = getClosestPeriodStartDate(
        optionsDateRange.startDate.date,
        billingFrequency,
        biweeklyCadenceStartDate,
    ).moment;

    const initialStartDateType = optionsDateRange.startDate.type;

    if (initialStartDateType === 'previous-plan-start-date') {
        return getNextPeriodStartDate(
            moment(optionsDateRange.startDate.date),
            billingFrequency,
            biweeklyCadenceStartDate,
        );
    }

    if (initialStartDateType === 'previous-plan-effective-date') {
        return closestPeriodStartDate;
    }

    // Whenever there is available dates before the current plan's start date (current or previous plan is not billed or no previous plan), we must show them as options
    // For now we only show 4 extra periods as options before the current plan's start date
    if (initialStartDateType === 'current-plan-start-date') {
        return closestPeriodStartDate
            .clone()
            .subtract(billingFrequency === 'biweekly' ? 4 * 2 : 4, billingFrequency === 'monthly' ? 'months' : 'weeks');
    }

    return closestPeriodStartDate;
};

/*
It generates a list of end date options based on selected start date.
It ensures that the initial plan end date is always included.
If the initial end date falls at the end of a billing period, we avoid duplication of dates in the dropdown.
The initial end date is only included if it occurs after the selected start date.
The resulting list of dates is sorted chronologically to ensure proper order when inserting the initial end date.
*/
const generatePlanEndDateOptions = ({
    dateRange,
    billingFrequency,
    biweeklyCadenceStartDate,
}: {
    dateRange: {startDate: Moment; endDate: Moment};
    billingFrequency: Types.InitialBillingFrequency;
    biweeklyCadenceStartDate: Moment;
}): SelectOption[] => {
    const optionsDateRange = {
        startDate: dateRange.startDate,
        // We must always show 53 extra periods as options after the current plan's end date
        endDate: moment(dateRange.endDate).add(
            billingFrequency === 'biweekly'
                ? DEFAULT_NUMBER_OF_OPTIONS_DISPLAYED * 2
                : DEFAULT_NUMBER_OF_OPTIONS_DISPLAYED,
            billingFrequency === 'monthly' ? 'months' : 'weeks',
        ),
    };
    const initialEndDateOption: SelectOption = {
        label: dateRange.endDate.format('MMMM D, YYYY'),
        value: dateRange.endDate.format('YYYY-MM-DD'),
    };

    const shouldIncludeInitialEndDate = isEndDateAfterStartDate(dateRange.endDate, dateRange.startDate);

    switch (billingFrequency) {
        case 'monthly': {
            const endOfPeriod = dateRange.endDate.clone().endOf('month');

            const options = generateOptions({
                type: 'end',
                dateRange: optionsDateRange,
                unitOfTime: 'months',
                format: 'MMMM YYYY',
            });

            const sortedOptionsWithInitialEndDate = sortOptionsByDate([initialEndDateOption, ...options]);

            const isEndDateSameAsEndOfPeriod = isDateSameAsPeriodBoundary(dateRange.endDate, endOfPeriod);

            return !isEndDateSameAsEndOfPeriod && shouldIncludeInitialEndDate
                ? sortedOptionsWithInitialEndDate
                : options;
        }
        case 'weekly': {
            const endOfPeriod = dateRange.endDate.clone().endOf('isoWeek');

            const options = generateOptions({
                type: 'end',
                dateRange: optionsDateRange,
                unitOfTime: 'weeks',
                format: 'MMMM D, YYYY',
            });

            const sortedOptionsWithInitialEndDate = sortOptionsByDate([initialEndDateOption, ...options]);

            const isEndDateSameAsEndOfPeriod = isDateSameAsPeriodBoundary(dateRange.endDate, endOfPeriod);

            return !isEndDateSameAsEndOfPeriod && shouldIncludeInitialEndDate
                ? sortedOptionsWithInitialEndDate
                : options;
        }
        case 'biweekly': {
            const closestBiweeklyStartDate = getClosestBiweeklyStartDate(biweeklyCadenceStartDate, dateRange.endDate);

            const isInSameWeek = dateRange.endDate.isSame(closestBiweeklyStartDate, 'isoWeek');

            const endOfWeek = dateRange.endDate.clone().endOf('isoWeek');

            const endOfPeriod = isInSameWeek ? endOfWeek.add(1, 'weeks') : endOfWeek;

            const options = generateOptions({
                type: 'end',
                dateRange: optionsDateRange,
                unitOfTime: 'weeks',
                format: 'MMMM D, YYYY',
                intervalMultiplier: 2,
                biweeklyCadenceStartDate,
            });

            const sortedOptionsWithInitialEndDate = sortOptionsByDate([initialEndDateOption, ...options]);

            const isEndDateSameAsEndOfPeriod = isDateSameAsPeriodBoundary(dateRange.endDate, endOfPeriod);

            return !isEndDateSameAsEndOfPeriod && shouldIncludeInitialEndDate
                ? sortedOptionsWithInitialEndDate
                : options;
        }
        default:
            return [];
    }
};

const getNextPeriodStartDate = (
    initialStartDate: Moment,
    billingFrequency: Types.Plan['billingFrequency'],
    biweeklyCadenceStartDate: Moment,
) => {
    switch (billingFrequency) {
        case 'monthly': {
            return initialStartDate.clone().add(1, 'months').startOf('month');
        }
        case 'weekly': {
            return initialStartDate.clone().add(1, 'weeks').startOf('isoWeek');
        }
        case 'biweekly': {
            const closestBiweeklyStartDate = getClosestBiweeklyStartDate(biweeklyCadenceStartDate, initialStartDate);

            const isInSameWeek = initialStartDate.isSame(closestBiweeklyStartDate, 'isoWeek');

            const weekStartDate = initialStartDate.clone().startOf('isoWeek');

            return isInSameWeek ? weekStartDate.clone().add(2, 'weeks') : weekStartDate.clone().add(1, 'weeks');
        }
        default:
            return initialStartDate;
    }
};

/**
 * Returns the nearest period aligned date **at or after** `initialStartDate`.
 */
const getClosestPeriodStartDate = (
    initialStartDate: Moment,
    billingFrequency: Types.Plan['billingFrequency'],
    biweeklyCadenceStartDate: Moment,
): {text: string; moment: Moment} => {
    const calculateNextStartDate = (() => {
        switch (billingFrequency) {
            case 'monthly': {
                const isStartOfPeriod = initialStartDate.date() === 1;

                return isStartOfPeriod
                    ? initialStartDate
                    : getNextPeriodStartDate(initialStartDate, billingFrequency, biweeklyCadenceStartDate);
            }
            case 'weekly': {
                const isStartOfPeriod = initialStartDate.isoWeekday() === 1;

                return isStartOfPeriod
                    ? initialStartDate
                    : getNextPeriodStartDate(initialStartDate, billingFrequency, biweeklyCadenceStartDate);
            }
            case 'biweekly': {
                const closestBiweeklyStartDate = getClosestBiweeklyStartDate(
                    biweeklyCadenceStartDate,
                    initialStartDate,
                );

                const isInSameWeek = initialStartDate.isSame(closestBiweeklyStartDate, 'isoWeek');

                const weekStartDate = initialStartDate.clone().startOf('isoWeek');

                const isFirstDayOfWeek = initialStartDate.isoWeekday() === 1;

                const closestStartDate = isInSameWeek ? weekStartDate.clone() : weekStartDate.clone().add(1, 'weeks');

                return isFirstDayOfWeek
                    ? closestStartDate
                    : getNextPeriodStartDate(initialStartDate, billingFrequency, biweeklyCadenceStartDate);
            }
            default:
                return initialStartDate;
        }
    })();

    return {
        text: calculateNextStartDate.format('YYYY-MM-DD'),
        moment: calculateNextStartDate,
    };
};

const formatPlanStartDate = (billingFrequency: Types.InitialBillingFrequency, startDate: string): string => {
    const startOfMonth = moment(startDate).startOf('month');
    const isStartDateSameAsStartOfMonth = isDateSameAsPeriodBoundary(moment(startDate), startOfMonth);

    return billingFrequency === 'monthly' && isStartDateSameAsStartOfMonth
        ? moment(startDate).format('MMMM YYYY')
        : moment(startDate).format('MMMM D, YYYY');
};

const formatPlanEndDate = (billingFrequency: Types.InitialBillingFrequency, endDate: string): string => {
    const endOfMonth = moment(endDate).endOf('month');
    const isEndDateSameAsEndOfMonth = isDateSameAsPeriodBoundary(moment(endDate), endOfMonth);

    return billingFrequency === 'monthly' && isEndDateSameAsEndOfMonth
        ? moment(endDate).format('MMMM YYYY')
        : moment(endDate).format('MMMM D, YYYY');
};

const isDateSameAsPeriodBoundary = (date: Moment, periodBoundary: Moment): boolean => {
    return date.isSame(periodBoundary, 'day');
};

const sortOptionsByDate = (options: Array<SelectOption>): Array<SelectOption> => {
    return options.sort((a, b) => moment(a.value).diff(moment(b.value)));
};

const isEndDateAfterStartDate = (endDate: Moment, selectedStartDate: Moment): boolean => {
    return endDate.isAfter(selectedStartDate);
};

export {
    getOptionsStartDate,
    getClosestPeriodStartDate,
    getNextPeriodStartDate,
    formatPlanStartDate,
    generatePlanStartDateOptions,
    generatePlanEndDateOptions,
    formatPlanEndDate,
};
